import {
  Box,
  Text,
  Button,
  VStack,
  Icon,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Flex,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { useConditionalBackground } from "@/contexts/useConditionalBackground ";
import { useAuthCorbanStore } from "@/contexts/useAuthCorbanStore";
import { registerAntiFraudLocation } from "@/services/antiFraud.service";
import { useEffect, useState } from "react";
import { MdLocationOn, MdLocationOff, MdInfo } from "react-icons/md";
import { IoMdRefresh } from "react-icons/io";

export default function PlatGeolocation() {
  const navigate = useNavigate();
  const backgroundStyle = useConditionalBackground();
  const { shorthash } = useParams<{ shorthash: string }>();
  const { userInfo } = useAuthCorbanStore();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);

  // Sem CPF validado não há processo de antifraude para vincular a localização.
  useEffect(() => {
    if (!userInfo?.user?.userId && shorthash) {
      navigate(`/plat`, { replace: true });
    }
  }, [navigate, shorthash, userInfo]);

  const requestGeolocation = () => {
    setIsLoading(true);
    setHasError(false);

    // Verifica se o navegador suporta geolocalização
    if (!navigator.geolocation) {
      handleGeolocationError("Geolocalização não é suportada pelo seu navegador");
      return;
    }

    // Solicita a localização do usuário
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        try {
          await registerAntiFraudLocation({
            userId: userInfo?.user?.userId ?? "",
            latitude,
            longitude,
          });
        } catch {
          setIsLoading(false);
          toast({
            title: "Não foi possível salvar sua localização",
            description: "Tente novamente para continuar.",
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "top",
          });
          return;
        }

        setIsLoading(false);

        toast({
          title: "Localização obtida com sucesso!",
          status: "success",
          duration: 2000,
          isClosable: true,
          position: "top",
        });

        // Navega para a próxima página após um breve delay
        setTimeout(() => {
          navigate(`/${shorthash}/plat/dados-pessoais`);
        }, 1000);
      },
      (error) => {
        let errorMessage = "Não foi possível obter sua localização";

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = "Você negou a permissão de localização";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Informações de localização não disponíveis";
            break;
          case error.TIMEOUT:
            errorMessage = "Tempo esgotado ao tentar obter localização";
            break;
        }

        handleGeolocationError(errorMessage);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  const handleGeolocationError = (message: string) => {
    setIsLoading(false);
    setHasError(true);
    console.error("Erro de geolocalização:", message);
  };

  const handleAgree = () => {
    requestGeolocation();
  };

  const handleShowInstructions = () => {
    setShowInstructionsModal(true);
  };

  const InstructionsModal = (
    <Modal
      isOpen={showInstructionsModal}
      onClose={() => setShowInstructionsModal(false)}
      size="xl"
      isCentered
    >
      <ModalOverlay />
      <ModalContent mx={4} maxHeight="90vh" overflowY="auto">
        <ModalBody p={6}>
          <VStack align="start" spacing={4}>
            <Flex justify="space-between" width="100%" align="center">
              <Text
                fontSize="lg"
                fontWeight="bold"
                color="gray.800"
                fontFamily="Lexend Deca"
              >
                Como ativar a localização
              </Text>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowInstructionsModal(false)}
              >
                ✕
              </Button>
            </Flex>

            <Box
              bg="green.50"
              p={3}
              borderRadius="md"
              width="100%"
              display="flex"
              alignItems="center"
              gap={3}
            >
              <Icon as={MdLocationOn} color="green.500" fontSize="2rem" />
              <Text fontSize="sm" color="green.700" fontFamily="Lexend Deca">
                Siga as instruções abaixo de acordo com seu dispositivo
              </Text>
            </Box>

            {/* Android - Chrome */}
            <Box width="100%">
              <Flex align="center" gap={2} mb={2}>
                <Box
                  bg="gray.700"
                  color="white"
                  px={3}
                  py={1}
                  borderRadius="md"
                  fontSize="xs"
                  fontWeight="bold"
                >
                  🤖 ANDROID
                </Box>
                <Text fontSize="sm" fontWeight="bold" color="gray.700">
                  Chrome
                </Text>
              </Flex>
              <VStack align="start" spacing={1} pl={4}>
                <Text fontSize="sm" color="gray.600">
                  1. Acesse o site pelo Chrome.
                </Text>
                <Text fontSize="sm" color="gray.600">
                  2. Toque no <strong>cadeado</strong> na barra de endereço.
                </Text>
                <Text fontSize="sm" color="gray.600">
                  3. Toque em <strong>Configurações do site</strong>.
                </Text>
                <Text fontSize="sm" color="gray.600">
                  4. Toque em <strong>Localização</strong>.
                </Text>
                <Text fontSize="sm" color="gray.600">
                  5. Altere de <strong>Bloquear</strong> para{" "}
                  <strong>Permitir</strong>.
                </Text>
              </VStack>
            </Box>

            {/* iPhone - Safari */}
            <Box width="100%" mt={3}>
              <Flex align="center" gap={2} mb={2}>
                <Box
                  bg="gray.700"
                  color="white"
                  px={3}
                  py={1}
                  borderRadius="md"
                  fontSize="xs"
                  fontWeight="bold"
                >
                  🍎 IPHONE
                </Box>
                <Text fontSize="sm" fontWeight="bold" color="gray.700">
                  Safari
                </Text>
              </Flex>
              <VStack align="start" spacing={1} pl={4}>
                <Text fontSize="sm" color="gray.600">
                  1. Acesse o site pelo Safari.
                </Text>
                <Text fontSize="sm" color="gray.600">
                  2. Toque no botão <strong>aA</strong> na barra de endereço.
                </Text>
                <Text fontSize="sm" color="gray.600">
                  3. Toque em <strong>Configurações do site</strong>.
                </Text>
                <Text fontSize="sm" color="gray.600">
                  4. Toque em <strong>Localização</strong>.
                </Text>
                <Text fontSize="sm" color="gray.600">
                  5. Selecione <strong>Permitir</strong>.
                </Text>
              </VStack>
            </Box>

            <Box bg="blue.50" p={3} borderRadius="md" width="100%" mt={2}>
              <Text fontSize="xs" color="blue.700" fontFamily="Lexend Deca">
                <strong>💡 Dica:</strong> Ainda com dúvidas? Consulte o manual do
                seu dispositivo.
              </Text>
            </Box>

            <Button
              width="100%"
              colorScheme="gray"
              borderRadius="full"
              py={5}
              onClick={() => setShowInstructionsModal(false)}
              mt={3}
              fontFamily="Lexend Deca"
            >
              Voltar
            </Button>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );

  if (hasError) {
    return (
      <>
        <Box
          minWidth="100vw"
          minHeight="100vh"
          {...backgroundStyle}
          display="flex"
          alignItems="flex-start"
          justifyContent="center"
          px={4}
        >
          <VStack
            mt="3rem"
            spacing={6}
            bg="white"
            p={8}
            borderRadius="lg"
            boxShadow="lg"
            width="100%"
            maxWidth="400px"
            border="3px solid"
            borderColor="primary"
          >
            <Box
              bg="red.50"
              p={4}
              borderRadius="full"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={MdLocationOff} color="red.500" fontSize="3rem" />
            </Box>

            <Text
              fontSize="xl"
              fontWeight="bold"
              color="gray.800"
              textAlign="center"
              fontFamily="Lexend Deca"
            >
              Não foi possível obter sua localização
            </Text>

            <Text
              fontSize="sm"
              color="gray.600"
              textAlign="center"
              fontFamily="Lexend Deca"
            >
              Você negou a permissão de localização. Por favor, habilite nas
              configurações do seu navegador.
            </Text>

            <Box
              bg="red.50"
              p={4}
              borderRadius="md"
              width="100%"
              border="1px solid"
              borderColor="red.200"
            >
              <Flex align="center" gap={2}>
                <Icon as={MdInfo} color="red.500" fontSize="1.2rem" />
                <Text fontSize="sm" color="red.700" fontFamily="Lexend Deca">
                  <strong>⚠️ A localização é obrigatória para continuar.</strong>{" "}
                  Por favor, habilite a permissão e tente novamente.
                </Text>
              </Flex>
            </Box>

            <VStack width="100%" spacing={3}>
              <Button
                width="100%"
                colorScheme="primary"
                borderRadius="full"
                py={6}
                onClick={requestGeolocation}
                leftIcon={<Icon as={IoMdRefresh} />}
                isLoading={isLoading}
                fontFamily="Lexend Deca"
              >
                Tentar Novamente
              </Button>

              <Button
                width="100%"
                variant="outline"
                colorScheme="primary"
                borderRadius="full"
                py={2}
                onClick={handleShowInstructions}
                leftIcon={<Icon as={MdInfo} />}
                fontFamily="Lexend Deca"
                whiteSpace="normal"
                height="auto"
              >
                Como Ativar a Localização
              </Button>
            </VStack>
          </VStack>
        </Box>
        {InstructionsModal}
      </>
    );
  }

  // Tela inicial - solicitação de permissão
  return (
    <>
      <Box
        minWidth="100vw"
        minHeight="100vh"
        {...backgroundStyle}
        display="flex"
        alignItems="flex-start"
        justifyContent="center"
        px={4}
      >
        <VStack
          mt="3rem"
          spacing={6}
          bg="white"
          p={8}
          borderRadius="lg"
          boxShadow="lg"
          width="100%"
          maxWidth="400px"
          border="3px solid"
          borderColor="primary"
        >
          <Box
            bg="green.50"
            p={4}
            borderRadius="full"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={MdLocationOn} color="primary" fontSize="3rem" />
          </Box>

          <Text
            fontSize="xl"
            fontWeight="bold"
            color="gray.800"
            textAlign="center"
            fontFamily="Lexend Deca"
          >
            Olá, para continuar precisamos da sua localização!
          </Text>

          <Text
            fontSize="sm"
            color="gray.600"
            textAlign="center"
            fontFamily="Lexend Deca"
          >
            Ao clicar em concordo abaixo, irá aparecer uma janela e nela
            selecione a opção "Permitir desta vez"
          </Text>

          <Button
            width="100%"
            colorScheme="primary"
            borderRadius="full"
            py={2}
            onClick={handleAgree}
            isLoading={isLoading}
            loadingText="Obtendo localização..."
            fontFamily="Lexend Deca"
          >
            Concordo!
          </Button>

          <Text
            fontSize="xs"
            color="gray.500"
            textAlign="center"
            fontFamily="Lexend Deca"
          >
            Caso não saiba como ativar sua localização, clique no botão abaixo
            para ver as instruções
          </Text>

          <Button
            width="100%"
            variant="outline"
            colorScheme="primary"
            borderRadius="full"
            py={2}
            onClick={handleShowInstructions}
            fontFamily="Lexend Deca"
            whiteSpace="normal"
            height="auto"
          >
            Como Ativar a Localização
          </Button>
        </VStack>
      </Box>
      {InstructionsModal}
    </>
  );
}
